import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Send,
  Loader2,
  Plus,
  Trash2,
  User,
  Mail,
  Phone,
  Home,
  MapPin,
  DollarSign,
  Clock,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronDown,
  Search,
  Shield,
} from "lucide-react";
import {
  sendLeadToInsurance,
  LOAN_PURPOSE_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  TIMELINE_OPTIONS,
} from "../services/LeadsService";
import { fetchPartners } from "../api/partners";

/**
 * SendToInsuranceModal - Modal to send lead information to insurance agent(s)
 */
const SendToInsuranceModal = ({
  isOpen,
  onClose,
  lead,
}) => {
  const [agents, setAgents] = useState([{ email: "", name: "", isSelected: false }]);
  const ccEmail = "info@cloudmortgage.io"; // Fixed CC email
  const [formData, setFormData] = useState({
    buyer_name: "",
    buyer_email: "",
    buyer_phone: "",
    loan_purpose: "Purchase",
    property_type: "Single Family",
    property_location: "",
    price_range: "",
    timeline: "Within 3 months",
    additional_notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [searchTerms, setSearchTerms] = useState({});
  const dropdownRefs = useRef({});

  // Pre-fill form with lead data when modal opens
  useEffect(() => {
    if (isOpen && lead) {
      // Get loan amount from various possible locations
      const loanAmount = lead.loan_details?.estimatedValue || lead.estimated_value || lead.loan_amount;

      // Get property location from address or state
      const propertyLocation = lead.loan_details?.propertyAddress ||
        (lead.state ? `${lead.city ? lead.city + ", " : ""}${lead.state}` : "");

      // Format price range
      let priceRange = "";
      if (loanAmount) {
        const amount = Number(String(loanAmount).replace(/[^0-9.-]/g, ""));
        if (!isNaN(amount) && amount > 0) {
          priceRange = `$${amount.toLocaleString()}`;
        }
      }

      setFormData({
        buyer_name: lead.contact_name || "",
        buyer_email: lead.contact_email || "",
        buyer_phone: lead.contact_phone || "",
        loan_purpose: lead.loan_details?.loanPurpose || "Purchase",
        property_type: lead.loan_details?.propertyType || lead.property_type || "Single Family",
        property_location: propertyLocation,
        price_range: priceRange,
        timeline: "Within 3 months",
        additional_notes: lead.loan_details?.additionalNotes || "",
      });
      setAgents([{ email: "", name: "", isSelected: false }]);
      setResults(null);
      setError(null);
      setOpenDropdownIndex(null);
      setSearchTerms({});
    }
  }, [isOpen, lead]);

  // Fetch available insurance agent partners when modal opens
  useEffect(() => {
    const loadAgents = async () => {
      if (!isOpen) return;
      setLoadingAgents(true);
      try {
        const response = await fetchPartners();
        const partners = response?.data?.partners || response?.partners || [];
        // Filter only insurance_agent type partners that are active
        const insurancePartners = partners.filter(
          (p) => p.partner_type === "insurance_agent" && p.status === "active"
        );
        setAvailableAgents(insurancePartners);
      } catch (err) {
        console.error("Failed to load insurance partners:", err);
        setAvailableAgents([]);
      } finally {
        setLoadingAgents(false);
      }
    };
    loadAgents();
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownIndex !== null) {
        const dropdownRef = dropdownRefs.current[openDropdownIndex];
        if (dropdownRef && !dropdownRef.contains(event.target)) {
          setOpenDropdownIndex(null);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownIndex]);

  const handleAgentChange = (index, field, value) => {
    const updated = [...agents];
    updated[index][field] = value;
    // If user manually edits email or name, mark as not selected from list
    if (field === "email" || field === "name") {
      updated[index].isSelected = false;
    }
    setAgents(updated);
  };

  const handleSelectAgent = (index, partner) => {
    const updated = [...agents];
    updated[index] = {
      email: partner.email,
      name: partner.name || partner.email.split("@")[0],
      isSelected: true,
    };
    setAgents(updated);
    setOpenDropdownIndex(null);
    setSearchTerms((prev) => ({ ...prev, [index]: "" }));
  };

  const handleClearSelection = (index) => {
    const updated = [...agents];
    updated[index] = { email: "", name: "", isSelected: false };
    setAgents(updated);
  };

  const addAgent = () => {
    if (agents.length < 20) {
      setAgents([...agents, { email: "", name: "", isSelected: false }]);
    }
  };

  const removeAgent = (index) => {
    if (agents.length > 1) {
      setAgents(agents.filter((_, i) => i !== index));
      // Clean up search term for removed index
      setSearchTerms((prev) => {
        const newTerms = { ...prev };
        delete newTerms[index];
        return newTerms;
      });
    }
  };

  const getFilteredAgents = (index) => {
    const searchTerm = (searchTerms[index] || "").toLowerCase();
    // Filter out already selected agents
    const selectedEmails = agents
      .filter((_, i) => i !== index)
      .map((r) => r.email.toLowerCase());

    return availableAgents.filter((partner) => {
      const isAlreadySelected = selectedEmails.includes(partner.email.toLowerCase());
      if (isAlreadySelected) return false;

      if (!searchTerm) return true;

      const nameMatch = (partner.name || "").toLowerCase().includes(searchTerm);
      const emailMatch = partner.email.toLowerCase().includes(searchTerm);
      const companyMatch = (partner.company || "").toLowerCase().includes(searchTerm);
      return nameMatch || emailMatch || companyMatch;
    });
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    // Validate agents
    const validAgents = agents.filter((r) => r.email && r.name);
    if (validAgents.length === 0) {
      setError("Please add at least one insurance agent with email and name");
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const agent of validAgents) {
      if (!emailRegex.test(agent.email)) {
        setError(`Invalid email format: ${agent.email}`);
        return false;
      }
    }

    // Validate required fields
    if (!formData.buyer_name.trim()) {
      setError("Buyer name is required");
      return false;
    }
    if (!formData.buyer_email.trim() || !emailRegex.test(formData.buyer_email)) {
      setError("Valid buyer email is required");
      return false;
    }
    if (!formData.buyer_phone.trim() || formData.buyer_phone.length < 10) {
      setError("Valid buyer phone is required (10+ characters)");
      return false;
    }
    if (!formData.property_location.trim()) {
      setError("Property location is required");
      return false;
    }
    if (!formData.price_range.trim()) {
      setError("Price range is required");
      return false;
    }

    // Validate notes length
    if (formData.additional_notes.length > 1000) {
      setError("Additional notes must be under 1000 characters");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const validAgents = agents.filter((r) => r.email && r.name);
      const response = await sendLeadToInsurance({
        agents: validAgents,
        cc_emails: [ccEmail], // CC referral manager on all insurance agent emails
        ...formData,
      });
      setResults(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setAgents([{ email: "", name: "", isSelected: false }]);
    setFormData({
      buyer_name: "",
      buyer_email: "",
      buyer_phone: "",
      loan_purpose: "Purchase",
      property_type: "Single Family",
      property_location: "",
      price_range: "",
      timeline: "Within 3 months",
      additional_notes: "",
    });
    setError(null);
    setResults(null);
    setOpenDropdownIndex(null);
    setSearchTerms({});
    onClose();
  };

  if (!isOpen) return null;

  // Show results after successful submission
  if (results) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/80 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          <div className="p-6">
            <div className="text-center mb-6">
              {results.total_failed === 0 ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                    Successfully Sent!
                  </h2>
                  <p className="text-slate-600 dark:text-slate-400">
                    {results.message}
                  </p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                    Partially Sent
                  </h2>
                  <p className="text-slate-600 dark:text-slate-400">
                    {results.total_sent} sent, {results.total_failed} failed
                  </p>
                </>
              )}
            </div>

            {/* Results list */}
            <div className="space-y-2 mb-6 max-h-48 overflow-y-auto">
              {results.results?.map((result, idx) => (
                <div
                  key={idx}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    result.success
                      ? "bg-emerald-50 dark:bg-emerald-900/20"
                      : "bg-red-50 dark:bg-red-900/20"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {result.success ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    )}
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white text-sm">
                        {result.name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {result.email}
                      </p>
                    </div>
                  </div>
                  {!result.success && result.error && (
                    <span className="text-xs text-red-600 dark:text-red-400">
                      {result.error}
                    </span>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={handleClose}
              className="w-full px-4 py-2.5 bg-sky-600 hover:bg-sky-500 text-white rounded-lg transition-colors font-medium"
            >
              Done
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/80 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                  Send to Insurance Agent
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Share this lead with insurance agent partners
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Agents Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Insurance Agent(s) to Send To
                  </label>
                  <span className="text-xs text-slate-500">
                    {agents.length}/20 max
                  </span>
                </div>
                <div className="space-y-3">
                  {agents.map((agent, idx) => (
                    <div
                      key={idx}
                      ref={(el) => (dropdownRefs.current[idx] = el)}
                      className="relative"
                    >
                      {/* Selected Agent Display */}
                      {agent.isSelected ? (
                        <div className="flex gap-3">
                          <div className="flex-1 flex items-center gap-3 px-3 py-2.5 border border-orange-300 dark:border-orange-700 rounded-lg bg-orange-50 dark:bg-orange-900/20">
                            <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-medium text-sm">
                              {(agent.name || agent.email)[0].toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 dark:text-white text-sm truncate">
                                {agent.name}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                {agent.email}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleClearSelection(idx)}
                              className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          {agents.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeAgent(idx)}
                              className="p-2.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <>
                          {/* Dropdown Trigger or Manual Input */}
                          <div className="flex gap-3">
                            <div className="flex-1">
                              <div className="relative">
                                {availableAgents.length > 0 || loadingAgents ? (
                                  <>
                                    {loadingAgents ? (
                                      <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-orange-500 animate-spin" />
                                    ) : (
                                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    )}
                                    <input
                                      type="text"
                                      value={
                                        openDropdownIndex === idx
                                          ? searchTerms[idx] || ""
                                          : agent.email
                                      }
                                      onChange={(e) => {
                                        if (openDropdownIndex === idx) {
                                          setSearchTerms((prev) => ({
                                            ...prev,
                                            [idx]: e.target.value,
                                          }));
                                        } else {
                                          handleAgentChange(idx, "email", e.target.value);
                                        }
                                      }}
                                      onFocus={() => setOpenDropdownIndex(idx)}
                                      placeholder={loadingAgents ? "Loading agents..." : "Search or type email..."}
                                      className="w-full pl-10 pr-10 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    />
                                    {loadingAgents ? (
                                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-orange-500 font-medium">
                                        Loading...
                                      </span>
                                    ) : (
                                      <ChevronDown
                                        className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 transition-transform ${
                                          openDropdownIndex === idx ? "rotate-180" : ""
                                        }`}
                                      />
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <input
                                      type="email"
                                      value={agent.email}
                                      onChange={(e) =>
                                        handleAgentChange(idx, "email", e.target.value)
                                      }
                                      placeholder="agent@email.com"
                                      className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    />
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                  type="text"
                                  value={agent.name}
                                  onChange={(e) =>
                                    handleAgentChange(idx, "name", e.target.value)
                                  }
                                  placeholder="Agent Name"
                                  className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                />
                              </div>
                            </div>
                            {agents.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeAgent(idx)}
                                className="p-2.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          {/* Dropdown Menu */}
                          {openDropdownIndex === idx && (availableAgents.length > 0 || loadingAgents) && (
                            <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {loadingAgents ? (
                                <div className="p-4 text-center text-slate-500 dark:text-slate-400">
                                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-orange-500" />
                                  Loading insurance partners...
                                </div>
                              ) : getFilteredAgents(idx).length > 0 ? (
                                <>
                                  <div className="px-3 py-2 text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                                    Select from your insurance partners
                                  </div>
                                  {getFilteredAgents(idx).map((partner) => (
                                    <button
                                      key={partner.email}
                                      type="button"
                                      onClick={() => handleSelectAgent(idx, partner)}
                                      className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors text-left"
                                    >
                                      <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                                        {(partner.name || partner.email)[0].toUpperCase()}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-slate-900 dark:text-white text-sm truncate">
                                          {partner.name || partner.email.split("@")[0]}
                                        </p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                          {partner.email}
                                          {partner.company && ` · ${partner.company}`}
                                        </p>
                                      </div>
                                      <Shield className="w-4 h-4 text-orange-500 flex-shrink-0" />
                                    </button>
                                  ))}
                                </>
                              ) : (
                                <div className="px-3 py-4 text-center text-slate-500 dark:text-slate-400 text-sm">
                                  {searchTerms[idx]
                                    ? "No matching partners found"
                                    : "No more available partners"}
                                  <p className="text-xs mt-1">
                                    Type an email to add manually
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
                {agents.length < 20 && (
                  <button
                    type="button"
                    onClick={addAgent}
                    className="mt-3 flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Add Another Agent
                  </button>
                )}

                {/* CC Email Field - Fixed value */}
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    CC
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      value={ccEmail}
                      readOnly
                      className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 cursor-not-allowed"
                    />
                  </div>
                  <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                    CloudMortgage will be CC'd on all insurance notifications
                  </p>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-slate-200 dark:border-slate-700" />

              {/* Buyer Information */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  Buyer Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={formData.buyer_name}
                        onChange={(e) =>
                          handleInputChange("buyer_name", e.target.value)
                        }
                        placeholder="John Smith"
                        className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Email *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="email"
                        value={formData.buyer_email}
                        onChange={(e) =>
                          handleInputChange("buyer_email", e.target.value)
                        }
                        placeholder="buyer@email.com"
                        className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Phone *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="tel"
                        value={formData.buyer_phone}
                        onChange={(e) =>
                          handleInputChange("buyer_phone", e.target.value)
                        }
                        placeholder="(555) 123-4567"
                        className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Loan Purpose *
                    </label>
                    <select
                      value={formData.loan_purpose}
                      onChange={(e) =>
                        handleInputChange("loan_purpose", e.target.value)
                      }
                      className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      {LOAN_PURPOSE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Property Information */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  Property Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Property Type *
                    </label>
                    <div className="relative">
                      <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <select
                        value={formData.property_type}
                        onChange={(e) =>
                          handleInputChange("property_type", e.target.value)
                        }
                        className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none"
                      >
                        {PROPERTY_TYPE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Location *
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={formData.property_location}
                        onChange={(e) =>
                          handleInputChange("property_location", e.target.value)
                        }
                        placeholder="Houston, TX"
                        className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Price Range *
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={formData.price_range}
                        onChange={(e) =>
                          handleInputChange("price_range", e.target.value)
                        }
                        placeholder="$300,000 - $400,000"
                        className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Timeline *
                    </label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <select
                        value={formData.timeline}
                        onChange={(e) =>
                          handleInputChange("timeline", e.target.value)
                        }
                        className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none"
                      >
                        {TIMELINE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                  Additional Notes (Optional)
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                  <textarea
                    value={formData.additional_notes}
                    onChange={(e) =>
                      handleInputChange("additional_notes", e.target.value)
                    }
                    placeholder="First-time homebuyer, needs homeowners insurance quote..."
                    rows={3}
                    maxLength={1000}
                    className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1 text-right">
                  {formData.additional_notes.length}/1000
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2.5 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send to Agent{agents.filter((r) => r.email && r.name).length > 1 ? "s" : ""}
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SendToInsuranceModal;
